import React from "react";
import PropTypes from "prop-types";

// styles
import * as styles from "./ToggleButton.module.css";

const ToggleButton = ({
  options,
  activeOption,
  onToggle,
  colors,
}) => {
  const getButtonStyle = (isActive) => {
    if (!colors) return {};

    if (isActive) {
      return {
        background: colors.activeBackground || undefined,
        borderColor: colors.activeBorder || undefined,
        color: colors.activeText || undefined,
      };
    }

    return {
      background: colors.inactiveBackground || undefined,
      borderColor: colors.inactiveBorder || undefined,
      color: colors.inactiveText || undefined,
    };
  };

  const getHoverStyle = () => {
    if (!colors?.hover) return {};

    return {
      "--hover-bg": colors.hover.background || undefined,
      "--hover-border": colors.hover.borderColor || undefined,
      "--hover-text": colors.hover.textColor || undefined,
    };
  };

  return (
    <div className={styles.toggleContainer}>
      {options.map((option, index) => {
        const isActive = activeOption === option.value;
        return (
          <button
            key={index}
            className={`${styles.toggleButton} ${
              isActive ? styles.active : ""
            }`}
            style={{
              ...getButtonStyle(isActive),
              ...getHoverStyle(),
            }}
            onClick={() => onToggle(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

ToggleButton.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  activeOption: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  colors: PropTypes.shape({
    // Active state colors
    activeBackground: PropTypes.string,
    activeBorder: PropTypes.string,
    activeText: PropTypes.string,
    // Inactive state colors
    inactiveBackground: PropTypes.string,
    inactiveBorder: PropTypes.string,
    inactiveText: PropTypes.string,
    // Hover state colors
    hover: PropTypes.shape({
      background: PropTypes.string,
      borderColor: PropTypes.string,
      textColor: PropTypes.string,
    }),
  }),
};

ToggleButton.defaultProps = {
  colors: null, // Uses default CSS styling when null
};

export default ToggleButton;

/**
 * ToggleButton Component Usage Examples
 * 
 * 1. Basic Usage (Default Colors):
 * ----------------------------------
 * <ToggleButton
 *   options={[
 *     { label: "Blog", value: "blog" },
 *     { label: "News", value: "news" }
 *   ]}
 *   activeOption={activeFilter}
 *   onToggle={handleToggle}
 * />
 * 
 * 2. Custom Colors Usage:
 * -----------------------
 * <ToggleButton
 *   options={options}
 *   activeOption={activeFilter}
 *   onToggle={handleToggle}
 *   colors={{
 *     // Active state (selected button)
 *     activeBackground: "linear-gradient(90deg, #c73b93 0%, #9b2c73 100%)",
 *     activeBorder: "#c73b93",
 *     activeText: "#ffffff",
 *     
 *     // Inactive state (unselected buttons)
 *     inactiveBackground: "#ffffff",
 *     inactiveBorder: "#e0e0e0",
 *     inactiveText: "#373049",
 *     
 *     // Hover state
 *     hover: {
 *       background: "#fef5fb",
 *       borderColor: "#c73b93",
 *       textColor: "#c73b93"
 *     }
 *   }}
 * />
 * 
 * 3. Different Color Scheme Example:
 * -----------------------------------
 * <ToggleButton
 *   options={options}
 *   activeOption={activeFilter}
 *   onToggle={handleToggle}
 *   colors={{
 *     activeBackground: "linear-gradient(90deg, #0b5464 0%, #084450 100%)",
 *     activeBorder: "#0b5464",
 *     activeText: "#ffffff",
 *     inactiveBackground: "#f5f5f5",
 *     inactiveBorder: "#cccccc",
 *     inactiveText: "#333333",
 *     hover: {
 *       background: "#e8f4f6",
 *       borderColor: "#0b5464",
 *       textColor: "#0b5464"
 *     }
 *   }}
 * />
 * 
 * 4. Partial Color Override:
 * --------------------------
 * You can override only specific colors, others will use defaults:
 * 
 * <ToggleButton
 *   options={options}
 *   activeOption={activeFilter}
 *   onToggle={handleToggle}
 *   colors={{
 *     activeBackground: "#ff6b6b",
 *     activeText: "#ffffff"
 *     // Other colors will use CSS defaults
 *   }}
 * />
 */
